import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { logoutAsync } from '../../slices/authSlice';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpDownIcon, PowerIcon } from '@heroicons/react/24/solid';
import { BellIcon, Bars3Icon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { menuConfig } from './const';
import { flattenNavigableLeaves, filterNavigableLeaves, getVisibleMenu } from './navigation';
import { useClickOutsideBlock } from '../../helpers/useClickOutside';
import { ROUTES } from '../../consts/general';
import { NotificationCenter } from '../notifications/NotificationCenter';

const SEARCH_SHORTCUT_KEY = '/';
const SEARCH_SHORTCUT_LABEL = SEARCH_SHORTCUT_KEY;
const SECONDARY_SEARCH_SHORTCUT_KEY = 'k';

const parseNavPath = (path) => {
  if (!path) return { pathname: '', search: '' };
  const qIndex = path.indexOf('?');
  return qIndex < 0
    ? { pathname: path, search: '' }
    : { pathname: path.slice(0, qIndex), search: path.slice(qIndex + 1) };
};

const getSidebarNavIsActive = (path, location) => {
  const { pathname, search } = parseNavPath(path);
  if (location.pathname !== pathname) return false;
  const expected = new URLSearchParams(search);
  const current = new URLSearchParams(location.search);
  for (const [key, value] of expected.entries()) {
    if (pathname === '/demand-planning' && key === 'id') continue;
    if (current.get(key) !== value) return false;
  }
  if (expected.has('view') || expected.has('tab')) return true;
  return !current.has('view') && !current.has('tab');
};

const resolveNavTarget = (path, location) => {
  const { pathname, search } = parseNavPath(path);
  if (pathname !== '/demand-planning') {
    return { pathname, search: search ? `?${search}` : '' };
  }
  const linkParams = new URLSearchParams(search);
  const nextParams = new URLSearchParams(linkParams);
  const currentParams = new URLSearchParams(location.search);
  const idToUse = currentParams.get('id') || linkParams.get('id') || '2';
  nextParams.set('id', idToUse);
  return { pathname, search: `?${nextParams.toString()}` };
};

const SidebarNavItem = ({ path, className, children, onClick, onMouseEnter, onMouseLeave, title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const target = resolveNavTarget(path, location);
  const handleClick = (event) => {
    onClick?.(event);
    if (event.defaultPrevented) return;
    event.preventDefault();
    if (location.pathname === target.pathname && location.search === target.search) return;
    navigate(target);
  };
  return (
    <a
      href={`${target.pathname}${target.search}`}
      title={title}
      className={className}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </a>
  );
};


const getDefaultPath = (node) => {
  if (node.type === 'leaf' || (!node.children && node.path)) return node.path;
  for (const child of node.children || []) {
    const path = getDefaultPath(child);
    if (path) return path;
  }
  return null;
};

const getNodeAncestors = (nodes, location, ancestors = []) => {
  for (const node of nodes) {
    if (node.path && getSidebarNavIsActive(node.path, location)) return ancestors;
    if (node.children) {
      const found = getNodeAncestors(node.children, location, [...ancestors, node.id]);
      if (found) return found;
    }
  }
  return null;
};

export { flattenNavigableLeaves, filterNavigableLeaves } from './navigation';

const Sidebar = ({
  testId,
  testData,
  isSidebarOpen,
  setIsSidebarOpen,
  childrenIdsAccess,
  parentIdsAccess,
  isMobile = false,
  menu = menuConfig,
}) => {
  const { currentUser } = useSelector((state) => state.auth);
  const notificationsState = useSelector((state) => state.notifications);
  const currentEnvironmentVersion = process.env.REACT_APP_KL_ENV;
  const user = testId && testData?.currentUser ? testData.currentUser : currentUser;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [hoverSection, setHoverSection] = useState(null);
  const [flyoutAnchor, setFlyoutAnchor] = useState(null);
  const [flyoutPosition, setFlyoutPosition] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSearchIndex, setSelectedSearchIndex] = useState(0);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [expanded, setExpanded] = useState({});
  const userDropdownMenuRef = useRef(null);
  const flyoutPanelRef = useRef(null);
  const flyoutCloseTimerRef = useRef(null);
  const searchInputRef = useRef(null);
  const initialLocationRef = useRef(`${location.pathname}${location.search}`);
  const hasNavigatedRef = useRef(false);
  useClickOutsideBlock(userDropdownMenuRef, () => setShowUserDropdown(false));

  const permittedChildIds = useMemo(() => childrenIdsAccess || null, [childrenIdsAccess]);
  const permittedParentIds = useMemo(() => parentIdsAccess || null, [parentIdsAccess]);
  const visibleMenu = useMemo(() => {
    return getVisibleMenu(menu, { childrenIdsAccess: permittedChildIds, parentIdsAccess: permittedParentIds, user });
  }, [menu, permittedChildIds, permittedParentIds, user]);

  const searchResults = useMemo(
    () => filterNavigableLeaves(flattenNavigableLeaves(visibleMenu), searchQuery),
    [visibleMenu, searchQuery]
  );

  const openSearch = useCallback(() => {
    setSearchQuery('');
    setSelectedSearchIndex(0);
    setSearchOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
    setSelectedSearchIndex(0);
  }, []);

  const closeSearchAndMobileNav = useCallback(() => {
    closeSearch();
    setMobileNavOpen(false);
  }, [closeSearch]);

  useEffect(() => {
    if (!searchOpen) return undefined;
    searchInputRef.current?.focus();
    return undefined;
  }, [searchOpen]);

  useEffect(() => {
    setSelectedSearchIndex((current) => Math.min(current, Math.max(searchResults.length - 1, 0)));
  }, [searchResults.length]);

  useEffect(() => {
    const handleGlobalKeyDown = (event) => {
      const target = event.target;
      const isEditableTarget = target instanceof HTMLElement && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.getAttribute('role') === 'combobox' ||
        target.closest('[role="combobox"]') ||
        target.isContentEditable
      );
      const isSecondaryShortcut = (event.metaKey || event.ctrlKey)
        && event.key.toLowerCase() === SECONDARY_SEARCH_SHORTCUT_KEY;
      const isPrimaryShortcut = event.key === SEARCH_SHORTCUT_KEY;
      if (isPrimaryShortcut && event.defaultPrevented) return;
      if (isPrimaryShortcut || isSecondaryShortcut) {
        if (isEditableTarget) return;
        event.preventDefault();
        openSearch();
        return;
      }
      if (!searchOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSearch();
      } else if (event.key === 'ArrowDown' && searchResults.length) {
        event.preventDefault();
        setSelectedSearchIndex((current) => (current + 1) % searchResults.length);
      } else if (event.key === 'ArrowUp' && searchResults.length) {
        event.preventDefault();
        setSelectedSearchIndex((current) => (current - 1 + searchResults.length) % searchResults.length);
      } else if (event.key === 'Enter' && searchResults[selectedSearchIndex]) {
        event.preventDefault();
        navigate(resolveNavTarget(searchResults[selectedSearchIndex].path, location));
        closeSearchAndMobileNav();
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [closeSearch, closeSearchAndMobileNav, location, navigate, openSearch, searchOpen, searchResults, selectedSearchIndex]);

  const searchTrigger = (
    <button
      type="button"
      onClick={openSearch}
      className="inline-flex items-center gap-2 rounded-md p-1.5 text-gray-600 transition hover:bg-gray-200 hover:text-gray-900"
      aria-label="Search views"
    >
      <MagnifyingGlassIcon className="h-5 w-5 shrink-0" />
    </button>
  );

  const ancestors = useMemo(
    () => getNodeAncestors(visibleMenu, location) || [],
    [location, visibleMenu]
  );

  useEffect(() => {
    if (`${location.pathname}${location.search}` !== initialLocationRef.current) {
      hasNavigatedRef.current = true;
    }
    if (!hasNavigatedRef.current) return;
    if (!ancestors.length) return;
    setExpanded((current) => {
      const next = { ...current };
      ancestors.forEach((id) => { next[id] = true; });
      const exclusiveAncestors = ancestors.filter((id) =>
        ['demand-planning', 'replenishment-planning', 'inventory-capital-risk', 'planning-controls', 'demand-transformations'].includes(id)
      );
      if (exclusiveAncestors.length) {
        ['demand-planning', 'replenishment-planning', 'inventory-capital-risk', 'planning-controls', 'demand-transformations']
          .forEach((id) => { next[id] = exclusiveAncestors.includes(id); });
      }
      return next;
    });
  }, [ancestors, location.pathname, location.search]);

  useEffect(() => {
    try {
      localStorage.removeItem('sidebarExpandedNav');
    } catch {}
  }, []);

  useEffect(() => {
    setMobileNavOpen(false);
    setHoverSection(null);
    setFlyoutAnchor(null);
  }, [location.pathname, location.search]);

  useLayoutEffect(() => {
    if (!flyoutAnchor || !flyoutPanelRef.current) return;
    const panelHeight = flyoutPanelRef.current.getBoundingClientRect().height;
    const maxTop = Math.max(8, window.innerHeight - panelHeight - 8);
    setFlyoutPosition({
      left: Math.max(8, Math.min(flyoutAnchor.right + 1, window.innerWidth - 298)),
      top: Math.min(Math.max(8, flyoutAnchor.top), maxTop),
    });
  }, [flyoutAnchor, hoverSection]);

  useEffect(() => () => {
    if (flyoutCloseTimerRef.current) clearTimeout(flyoutCloseTimerRef.current);
  }, []);

  const toggleExpanded = (node, exclusive = false) => {
    setExpanded((current) => {
      const next = { ...current, [node.id]: !current[node.id] };
      if (exclusive && !current[node.id]) {
        node.parentChildren?.forEach((sibling) => {
          if (sibling.id !== node.id) next[sibling.id] = false;
        });
      }
      return next;
    });
  };

  const linkClass = (path, depth = 0) => {
    const active = getSidebarNavIsActive(path, location);
    return `flex min-w-0 items-center gap-2 rounded-md px-2.5 py-1 text-sm transition-colors ${
      depth === 0 ? '' : depth === 1 ? 'ml-3' : 'ml-6'
    } ${active ? 'bg-gray-200 font-semibold text-gray-900' : 'text-gray-700 hover:bg-gray-200/70 hover:text-gray-900'}`;
  };

  const renderIcon = (icon, className) => {
    if (!icon) return null;
    return React.isValidElement(icon) ? React.cloneElement(icon, { className }) : React.createElement(icon, { className });
  };

  const renderLeaf = (node, depth = 0) => {
    const Icon = node.icon;
    return (
      <SidebarNavItem key={`${node.id}-${node.key || node.path}`} path={node.path} className={linkClass(node.path, depth)}>
        {renderIcon(Icon, 'h-4 w-4 min-h-4 min-w-4 shrink-0')}
        <span className="truncate">{node.label}</span>
      </SidebarNavItem>
    );
  };

  const renderTreeNode = (node, depth = 0, siblings = []) => {
    if (node.type === 'leaf' || (!node.children && node.path)) return renderLeaf(node, depth);
    const Icon = node.icon;
    const isOpen = Boolean(expanded[node.id]);
    const defaultPath = getDefaultPath(node);
    const isInventorySubgroup = node.id === 'demand-planning' || node.id === 'replenishment-planning' ||
      node.id === 'inventory-capital-risk' || node.id === 'planning-controls' ||
      node.id === 'demand-transformations';
    const childNodes = node.children.map((child) =>
      renderTreeNode({ ...child, parentChildren: node.children }, depth + 1, node.children)
    );
    return (
      <li key={node.id} className="min-w-0">
        <div className={`flex items-center gap-1 ${depth === 0 ? 'mt-1' : ''}`}>
          <SidebarNavItem
            path={defaultPath}
            className={`${linkClass(defaultPath, depth)} flex-1`}
          >
            {renderIcon(Icon, 'h-4 w-4 min-h-4 min-w-4 shrink-0')}
            <span className="truncate">{node.label}</span>
          </SidebarNavItem>
          <button
            type="button"
            aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${node.label}`}
            aria-expanded={isOpen}
            onClick={() => toggleExpanded(node, isInventorySubgroup)}
            className="rounded-md p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-900"
          >
            <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {isOpen && <ul className="mt-1 space-y-1">{childNodes}</ul>}
      </li>
    );
  };

  const searchPalette = searchOpen ? createPortal(
    <div className="fixed inset-0 z-[220] flex items-start justify-center overflow-y-auto bg-gray-900/30 px-4 pt-[12vh]" onMouseDown={(event) => {
      if (event.target === event.currentTarget) closeSearch();
    }}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search views"
        className="w-full max-w-xl overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
      >
        <div className="flex items-center gap-2 border-b border-gray-200 px-4">
          <MagnifyingGlassIcon className="h-5 w-5 shrink-0 text-gray-400" />
          <input
            ref={searchInputRef}
            type="search"
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value);
              setSelectedSearchIndex(0);
            }}
            placeholder="Search views"
            aria-label="Search views"
            className="min-w-0 flex-1 border-0 py-3 text-sm text-gray-900 outline-none ring-0 placeholder:text-gray-400 focus:ring-0"
          />
          <kbd className="rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-400">{SEARCH_SHORTCUT_LABEL}</kbd>
          <kbd className="rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-400">Esc</kbd>
        </div>
        <div className="max-h-[min(60vh,28rem)] overflow-y-auto p-2">
          {searchResults.length ? (
            <ul role="listbox" aria-label="Navigation results" className="space-y-1">
              {searchResults.map((result, index) => (
                <li key={`${result.id}-${result.path}`} role="option" aria-selected={index === selectedSearchIndex}>
                  <SidebarNavItem
                    path={result.path}
                    onClick={closeSearchAndMobileNav}
                    className={`flex min-w-0 items-start gap-3 rounded-lg px-3 py-2 text-left transition-colors ${
                      index === selectedSearchIndex ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {renderIcon(result.icon, 'mt-0.5 h-4 w-4 shrink-0 text-gray-500')}
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{result.label}</span>
                      <span className="block truncate text-xs text-gray-500">{result.breadcrumb}</span>
                    </span>
                  </SidebarNavItem>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-3 py-8 text-center text-sm text-gray-500">No matching views</p>
          )}
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  const renderExpandedMenu = () => (
    <div className="flex flex-col gap-3">
      {visibleMenu.filter((node) => node.type === 'pinned').map((node) => renderLeaf(node))}
      {visibleMenu.filter((node) => node.type === 'section' || (!node.type && node.children)).map((section) => (
        <section key={section.id}>
          <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-widest text-gray-500">{section.label}</div>
          <ul className="space-y-1">{section.children.map((child) => renderTreeNode(child))}</ul>
        </section>
      ))}
    </div>
  );

  const cancelFlyoutClose = () => {
    if (flyoutCloseTimerRef.current) {
      clearTimeout(flyoutCloseTimerRef.current);
      flyoutCloseTimerRef.current = null;
    }
  };

  const scheduleFlyoutClose = () => {
    cancelFlyoutClose();
    flyoutCloseTimerRef.current = setTimeout(() => {
      setHoverSection(null);
      setFlyoutAnchor(null);
    }, 200);
  };

  const openFlyout = (sectionId, event) => {
    cancelFlyoutClose();
    const rect = event.currentTarget.getBoundingClientRect();
    setFlyoutAnchor(rect);
    setFlyoutPosition({
      left: Math.max(8, Math.min(rect.right + 1, window.innerWidth - 298)),
      top: rect.top,
    });
    setHoverSection(sectionId);
  };

  const renderFlyout = (section) => {
    if (!flyoutPosition) return null;
    return createPortal(
      <div
        ref={flyoutPanelRef}
        className="fixed z-[200] max-h-[calc(100vh-1rem)] w-[290px] overflow-y-auto rounded-lg border border-gray-200 bg-white p-3 text-gray-700 shadow-xl"
        style={{ left: flyoutPosition.left, top: flyoutPosition.top }}
        onMouseEnter={cancelFlyoutClose}
        onMouseLeave={scheduleFlyoutClose}
      >
        <div className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-widest text-gray-500">{section.label}</div>
        <ul className="space-y-1">{section.children.map((child) => renderTreeNode(child))}</ul>
      </div>,
      document.body
    );
  };

  const userBlock = (
    <div className="relative mt-2">
      <div className={`flex items-center justify-between gap-2 ${!isMobile && !isSidebarOpen ? 'flex-col-reverse' : ''}`}>
        <div className="relative" ref={userDropdownMenuRef}>
          <div
            className={`flex cursor-pointer items-center justify-between rounded-md p-2 hover:bg-gray-100 ${
              !isMobile && !isSidebarOpen ? 'aspect-square max-w-[34px] justify-center' : ''
            }`}
            onClick={() => setShowUserDropdown((prev) => !prev)}
          >
            <div className={`flex min-w-0 flex-1 flex-col ${(isMobile || isSidebarOpen) ? 'overflow-hidden' : ''}`}>
              <span className="truncate text-sm font-medium text-gray-600">
                {(isMobile || isSidebarOpen)
                  ? user?.full_name
                  : (user?.full_name || '').split(' ').filter(Boolean).map((word) => word[0]).join('').toUpperCase().slice(0, 2)}
              </span>
              {(isMobile || isSidebarOpen) && <span className="truncate text-xs text-gray-500">{user?.email}</span>}
            </div>
            {(isMobile || isSidebarOpen) && <ChevronUpDownIcon className="h-5 w-5 shrink-0 text-gray-500" />}
          </div>
          {showUserDropdown && (
            <div className={`absolute z-[1] flex w-[200px] flex-col rounded-md border border-gray-200 bg-white p-1 shadow-lg ${
              isMobile ? 'bottom-full left-0 mb-2' : `bottom-0 left-full ${isSidebarOpen ? 'ml-2' : 'ml-[15px]'}`
            }`}>
              <div className="flex flex-col px-1 py-1.5">
                <span className="truncate text-sm font-medium text-gray-600">{user?.full_name}</span>
                <span className="truncate text-xs text-gray-500">{user?.email}</span>
              </div>
              <div className="-mx-1 my-1 h-px bg-gray-100" />
              <NavLink to={ROUTES.NOTIFICATIONS} onClick={() => setShowUserDropdown(false)} className="flex items-center gap-2 rounded-md px-1 py-1.5 text-sm text-gray-700 hover:bg-gray-100">
                <BellIcon className="h-5 w-5" /> <span>Notifications</span>
              </NavLink>
              <NavLink to={ROUTES.NOTIFICATIONS_SETTINGS} onClick={() => setShowUserDropdown(false)} className="flex items-center gap-2 rounded-md px-1 py-1.5 text-sm text-gray-700 hover:bg-gray-100">
                <BellIcon className="h-5 w-5" /> <span>Notification settings</span>
              </NavLink>
              <div className="-mx-1 my-1 h-px bg-gray-100" />
              <button onClick={() => dispatch(logoutAsync())} className="flex items-center gap-2 rounded-md px-1 py-1.5 text-sm text-red-500 hover:bg-gray-100">
                <PowerIcon className="h-5 w-5" /> <span>Log out</span>
              </button>
            </div>
          )}
        </div>
        {notificationsState && <NotificationCenter />}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <header className="fixed left-0 right-0 top-0 z-[100] flex h-14 items-center justify-between border-b border-gray-200 bg-gray-50 px-3 shadow-sm" data-testid={testId}>
          <button type="button" onClick={() => setMobileNavOpen(true)} aria-label="Open navigation menu" className="rounded-md p-2 text-gray-700 hover:bg-gray-200">
            <Bars3Icon className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <img src="/KL_Logo_Wide.webp" alt="Kings Loot Logo" className="h-9" />
            {currentEnvironmentVersion === 'beta' && <span className="rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">BETA</span>}
          </div>
          <div className="w-8" />
        </header>
        {mobileNavOpen && (
          <div className="fixed inset-0 z-[110]">
            <div className="absolute inset-0 bg-black/40" onClick={() => setMobileNavOpen(false)} />
            <div className="absolute bottom-0 left-0 top-0 flex w-[300px] max-w-[85vw] flex-col border-r border-gray-200 bg-gray-50 px-2 py-4 shadow-xl">
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <img src="/KL_Logo_Wide.webp" alt="Kings Loot Logo" className="h-11" />
                  {currentEnvironmentVersion === 'beta' && <span className="rounded-md bg-blue-50 px-2 py-0.5 text-sm font-medium text-blue-700">BETA</span>}
                </div>
                <button type="button" onClick={() => setMobileNavOpen(false)} className="rounded-md p-2 text-gray-700 hover:bg-gray-200">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="px-1 pt-4">{searchTrigger}</div>
              <div className="sidebar-scrollbar mt-3 flex-1 overflow-y-auto pr-2">{renderExpandedMenu()}</div>
              {userBlock}
            </div>
          </div>
        )}
        {searchPalette}
      </>
    );
  }

  const sections = visibleMenu.filter((node) => node.type === 'section' || (!node.type && node.children));
  return (
    <>
      <aside className={`fixed bottom-0 left-0 top-0 z-[100] border-r border-gray-200 bg-gray-50 px-2 py-4 shadow-sm transition-all duration-300 ${isSidebarOpen ? 'w-[300px]' : 'w-[50px]'}`} data-testid={testId}>
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-center">
            {isSidebarOpen ? (
              <div className="flex items-center gap-2">
                <img src="/KL_Logo_Wide.webp" alt="Kings Loot Logo" className="h-11" />
                {currentEnvironmentVersion === 'beta' && <span className="rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">BETA</span>}
              </div>
            ) : <img src="/KL_Logo.webp" alt="Logo Icon" className="h-8" />}
            <button
              type="button"
              aria-label={isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="absolute -right-3 rounded-full border border-gray-300 bg-white p-1 shadow-sm"
            >
              {isSidebarOpen ? <ChevronLeftIcon className="h-4 w-4 text-gray-600" /> : <ChevronRightIcon className="h-4 w-4 text-gray-600" />}
            </button>
          </div>
          {isSidebarOpen ? (
              <div className="px-1 pt-4">{searchTrigger}</div>
          ) : (
            <button
              type="button"
              onClick={openSearch}
              aria-label="Search views"
              className="mt-4 flex w-full justify-center rounded-md p-1.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
            >
              <MagnifyingGlassIcon className="h-5 w-5" />
            </button>
          )}
          <div className="sidebar-scrollbar mt-3 flex-1 overflow-y-auto pr-2">
            {isSidebarOpen ? renderExpandedMenu() : (
              <ul className="space-y-1">
                {visibleMenu.filter((node) => node.type === 'pinned').map((node) => {
                  return <li key={node.id}><SidebarNavItem path={node.path} title={node.label} className={`flex justify-center rounded-md p-1.5 ${getSidebarNavIsActive(node.path, location) ? 'bg-gray-200 text-gray-900' : 'text-gray-600 hover:bg-gray-200/70 hover:text-gray-900'}`}>{renderIcon(node.icon, 'h-5 w-5')}</SidebarNavItem></li>;
                })}
                {sections.map((section) => {
                  const Icon = section.icon || BuildingOffice2Icon;
                  return (
                    <li key={section.id} className="relative" onMouseEnter={(event) => openFlyout(section.id, event)} onMouseLeave={scheduleFlyoutClose}>
                      <button type="button" aria-label={section.label} className="flex w-full justify-center rounded-md p-1.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900">
                        {renderIcon(Icon, 'h-5 w-5')}
                      </button>
                      {hoverSection === section.id && renderFlyout(section)}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {userBlock}
        </div>
      </aside>
      {searchPalette}
    </>
  );
};

export default Sidebar;
